import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { isSolarThermalImage } from '../../../utils/solarImages';
import { getAuthToken } from '../../../utils/authStorage';
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export function canOpenSolarImage({ enabled, loaded, starting, tasks }, imageId) {
  if (!enabled) return true;
  if (!loaded || starting) return false;
  const task = tasks.find(item => String(item.image_id) === String(imageId));
  if (task) return task.status === 'DONE';
  if (tasks.some(item => ['PENDING','RUNNING'].includes(item.status))) return false;
  return true;
}

export default function useSolarAnalysis({ inspectionId, images, enabled, selectedImageId, viewerOpen }) {
  const generationRef = useRef(0);
  const reviewBusyRef = useRef(false);
  const thermalImages = useMemo(() => images.filter(isSolarThermalImage), [images]);
  const rgbIds = useMemo(() => new Set(images.filter(image => !isSolarThermalImage(image)).map(image => String(image.image_id || image.imageId || image.id))), [images]);
  const [snapshot, setSnapshot] = useState({ tasks: [] });
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const base = `/api/inspections/${inspectionId}/solar-analysis`;
  useEffect(() => { setSnapshot({ tasks: [] }); setLoaded(false); setUnavailable(false); }, [inspectionId]);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController(); let timer;
    const generation = generationRef.current;
    async function poll() {
      try {
        const { data } = await axios.get(base, { ...auth(), signal: controller.signal });
        if (controller.signal.aborted || generation !== generationRef.current) return;
        setSnapshot(data); setLoaded(true); setUnavailable(false); setError('');
        if (data.tasks?.some(task => ['PENDING','RUNNING'].includes(task.status))) timer = setTimeout(poll, 4000);
      } catch (err) {
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const disabled = [404,503].includes(err.response?.status);
        if (disabled) { setLoaded(true); setUnavailable(true); }
        setError(disabled ? 'Solar analysis is not enabled on the server yet.' : 'Could not update analysis status. Retrying…');
        if (!disabled) timer = setTimeout(poll, 8000);
      }
    }
    poll(); return () => { controller.abort(); clearTimeout(timer); };
  }, [base, enabled, refresh]);
  const tasks = useMemo(() => snapshot.tasks.filter(task => !rgbIds.has(String(task.image_id))).map(task => task.id === retryingId ? { ...task, status: 'PENDING' } : task), [snapshot.tasks, retryingId, rgbIds]);
  const canOpen = useCallback(id => rgbIds.has(String(id)) || canOpenSolarImage({ enabled, loaded, starting, unavailable, tasks }, id), [enabled, loaded, starting, unavailable, tasks, rgbIds]);
  const task = tasks.find(item => String(item.image_id) === String(selectedImageId));
  const taskId = task?.id, taskStatus = task?.status;
  useEffect(() => {
    setDetail(null); setReviewError(''); setSelectedCandidateId(null); setDetailLoading(false);
    if (!enabled || !viewerOpen || taskStatus !== 'DONE') return;
    const controller = new AbortController(); setDetailLoading(true);
    axios.get(`${base}/tasks/${taskId}`, { ...auth(), signal: controller.signal }).then(({data}) => {
      if (controller.signal.aborted) return;
      const reviews = Object.fromEntries(data.reviews.map(item => [item.candidate_id,item.status]));
      setDetail({ taskId, imageId: selectedImageId, ...data.result, candidates: (data.result.candidates || []).map(candidate => ({ ...candidate, reviewStatus: reviews[candidate.id] || 'PENDING' })) });
      setDetailLoading(false);
    }).catch(() => { if (!controller.signal.aborted) { setReviewError('Could not load anomalies. Close and reopen the image to retry.'); setDetailLoading(false); } });
    return () => controller.abort();
  }, [base, enabled, viewerOpen, taskId, taskStatus, selectedImageId]);
  async function start(delta) {
    generationRef.current += 1;
    setStarting(true); setError('');
    try {
      const ids = thermalImages.map(image => image.image_id || image.imageId || image.id).filter(Boolean);
      if (!thermalImages.length) throw new Error('No thermal images found. Thermal filenames must end in _T before the extension.');
      if (!ids.length || ids.length !== thermalImages.length) throw new Error('Reload the inspection to load all image IDs.');
      await axios.post(base, { imageIds: ids, deltaC: Number(delta), minPixels: 4 }, auth());
      // Keep images locked until the new job status is known, even after POST completes.
      setLoaded(false); setSnapshot({tasks:[]}); setRefresh(value => value + 1);
    } catch (err) { setError(err.response?.data?.message || err.message || 'Could not start analysis.'); setRefresh(value => value + 1); }
    finally { setStarting(false); }
  }
  async function reviewMany(candidates, status) {
    if (reviewBusyRef.current || !candidates.length) return [];
    reviewBusyRef.current = true; setBusy(true); setReviewError('');
    const currentImage = selectedImageId, currentTask = taskId;
    const saved = []; let failures = 0;
    try {
      // Limit concurrency for large rectangle selections.
      for (let i = 0; i < candidates.length; i += 4) {
        const batch = candidates.slice(i, i + 4);
        const outcomes = await Promise.allSettled(batch.map(candidate => axios.patch(`${base}/tasks/${currentTask}/candidates/${candidate.id}`, {status}, auth())));
        const ids = new Set();
        for (const [index, outcome] of outcomes.entries()) {
          if (outcome.status === 'fulfilled') { ids.add(batch[index].id); saved.push(batch[index].id); } else failures++;
        }
        setDetail(current => current?.imageId === currentImage && current.taskId === currentTask ? { ...current, candidates: current.candidates.map(item => ids.has(item.id) ? {...item,reviewStatus:status} : item) } : current);
      }
      if (failures) setReviewError(`${failures} review(s) were not saved. Failed items are unchanged. Please try again.`);
      return saved;
    } finally { reviewBusyRef.current = false; setBusy(false); }
  }
  async function review(candidate, status) { return reviewMany([candidate], status); }
  async function addManual(box) {
    if(reviewBusyRef.current || taskStatus !== 'DONE') return false;
    reviewBusyRef.current=true;setBusy(true);setReviewError('');
    const currentImage=selectedImageId,currentTask=taskId;
    try {
      const {data}=await axios.post(`${base}/tasks/${currentTask}/manual-candidates`,box,auth());
      setDetail(current=>current?.imageId===currentImage && current.taskId===currentTask ? {...current,candidates:[...current.candidates,data]}:current);
      return true;
    } catch {setReviewError('Could not save the manual anomaly. Check thermal data and try again.');return false;}
    finally {reviewBusyRef.current=false;setBusy(false);}
  }
  async function retry(id) {
    setRetryingId(id); setError('');
    try {
      await axios.post(`${base}/tasks/${id}/retry`, {}, auth());
      setSnapshot(current => ({...current,tasks:current.tasks.map(item => item.id===id ? {...item,status:'PENDING'} : item)}));
      setRefresh(value => value + 1);
    } catch { setError('Could not retry this image.'); } finally { setRetryingId(null); }
  }
  return { snapshot, tasks, thermalImageCount: thermalImages.length, loaded, unavailable, error, starting, busy, detail, detailLoading, reviewError, selectedCandidateId, setSelectedCandidateId,
    canOpen, start, review, reviewMany, addManual, retry, refresh: () => setRefresh(value => value + 1), active: starting || tasks.some(item => ['PENDING','RUNNING'].includes(item.status)) };
}
